/**
 * The single HTTP entry point for the web app.
 *
 * Hand-rolled over `fetch`, matching the house convention (mercury's
 * `apps/web/src/utils/api.ts`): no axios, no client-cache library. The app's
 * data is small, mutable and always re-read after a mutation, so a cache layer
 * would add invalidation bugs without saving a request.
 *
 * The bearer token is supplied by a registered provider rather than read from
 * storage here, so the auth module owns refresh and this module never has to
 * know where a token lives.
 */
import type { Finding } from '@vulcan/flow-contract'

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly body: unknown,
  ) {
    super(message)
    this.name = 'ApiError'
  }

  /** 401/403 mean "re-authenticate"; everything else is a real failure. */
  get isAuthFailure(): boolean {
    return this.status === 401 || this.status === 403
  }
}

/**
 * The findings an error body carries. A 422 from `POST`/`PATCH /flows` says
 * the document is MALFORMED and names the offending paths, and that list is
 * the only explanation the author gets — so it is read out of the body rather
 * than replaced with the generic message.
 */
export function findingsOf(body: unknown): Finding[] {
  if (typeof body !== 'object' || body === null || !('findings' in body)) return []
  return Array.isArray(body.findings) ? (body.findings as Finding[]) : []
}

type TokenProvider = () => Promise<string | undefined> | string | undefined

let tokenProvider: TokenProvider = () => undefined
let onAuthFailure: (() => void) | undefined

export function registerTokenProvider(provider: TokenProvider): void {
  tokenProvider = provider
}

/** Called on a 401 so the auth module can clear the session and redirect. */
export function registerAuthFailureHandler(handler: () => void): void {
  onAuthFailure = handler
}

/**
 * The envelope every list endpoint answers.
 *
 * `total` counts the rows matching the filters, ignoring `limit`/`offset`.
 * Without it a pager cannot know how many pages exist, and a client that
 * infers "this was the last page" from a short answer is wrong precisely when
 * the total is an exact multiple of the page size.
 */
export interface Paginated<T> {
  items: T[]
  total: number
  limit: number
  offset: number
}

/**
 * The `limit` contract shared by the list endpoints: the API defaults to 25
 * and refuses anything outside 1…200, so a caller that wants "everything"
 * asks for `MAX_PAGE_SIZE` rather than omitting the parameter and silently
 * receiving one page.
 */
export const DEFAULT_PAGE_SIZE = 25
export const MAX_PAGE_SIZE = 200

const BASE_URL = (import.meta.env.VITE_API_BASE_URL ?? '/api').replace(/\/$/, '')

export interface ApiRequest {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'
  body?: unknown
  query?: Record<string, string | number | boolean | undefined | null>
  signal?: AbortSignal
}

export async function apiFetch<T>(path: string, request: ApiRequest = {}): Promise<T> {
  const url = new URL(`${BASE_URL}${path}`, window.location.origin)
  for (const [key, value] of Object.entries(request.query ?? {})) {
    if (value !== undefined && value !== null && value !== '') {
      url.searchParams.set(key, String(value))
    }
  }

  const headers: Record<string, string> = { Accept: 'application/json' }
  const token = await tokenProvider()
  if (token) headers.Authorization = `Bearer ${token}`
  if (request.body !== undefined) headers['Content-Type'] = 'application/json'

  const response = await fetch(url, {
    method: request.method ?? 'GET',
    headers,
    body: request.body === undefined ? undefined : JSON.stringify(request.body),
    signal: request.signal,
  })

  if (response.status === 204) return undefined as T

  const raw = await response.text()
  const payload = raw.length > 0 ? safeJson(raw) : undefined

  if (!response.ok) {
    if (response.status === 401) onAuthFailure?.()
    throw new ApiError(response.status, extractMessage(payload, response), payload)
  }
  return payload as T
}

function safeJson(raw: string): unknown {
  try {
    return JSON.parse(raw)
  } catch {
    // A proxy or gateway error page is HTML; surface it as text rather than
    // throwing a parse error that hides the real status.
    return raw
  }
}

function extractMessage(payload: unknown, response: Response): string {
  if (payload && typeof payload === 'object') {
    const record = payload as Record<string, unknown>
    if (typeof record.message === 'string') return record.message
    if (Array.isArray(record.message)) return record.message.join(', ')
  }
  return `${response.status} ${response.statusText}`
}
